import { useEffect, useState, ChangeEvent, useRef } from 'react';

import { IPaymentCardValues, IPaymentCardErrors } from '@origin-digital/payments';

/**
 * Hook that only runs once, allows for async usage
 */
type UseOnceReturnType = void | (() => void);
export const useOnce = (fn: () => UseOnceReturnType | Promise<UseOnceReturnType>): void => {
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const cleanup = fn();
    if (typeof cleanup === 'function') {
      return cleanup;
    }
  }, []);
};

/**
 * https://usehooks.com/usePrevious/
 *
 * Hook to get the previous value of props or state
 */
export const usePrevious = <T extends {}>(value: T): T => {
  const ref = useRef<T>();

  useEffect(
    () => {
      ref.current = value;
    },
    [value],
  );

  return ref.current;
};

/**
 * Hook to manage state of <TextField />
 */
export const useTextFieldState = (initialValue = '', initialHelperText = '') => {
  const [value, setValue] = useState<string>(initialValue);
  const [helperText, setHelperText] = useState<string>(initialHelperText);
  const [error, setError] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);

  return {
    error,
    focus,
    onFocus: () => {
      setFocus(true);
    },
    onBlur: () => {
      setFocus(false);
    },
    setError: (message: string) => {
      if (!!message) {
        setHelperText(message);
        setError(true);

        return;
      }

      setHelperText(initialHelperText);
      setError(false);
    },
    setValue: (eventOrValue: string | ChangeEvent<HTMLInputElement>) => {
      if (typeof eventOrValue === 'string') {
        setValue(eventOrValue);
        return;
      }

      setValue(eventOrValue.target.value);
    },
    helperText,
    value,
  };
};

/**
 * Hook to manage state of <PaymentCard />
 */
export const usePaymentCardState = (initialValues?: IPaymentCardValues) => {
  const [values, setValues] = useState<IPaymentCardValues>(initialValues);
  const [errors, setErrors] = useState<IPaymentCardErrors>();
  const [forceValidation, setForceValidation] = useState<boolean>(false);

  return {
    errors,
    forceValidation,
    values,
    setErrors,
    setForceValidation,
    setValues,
  };
};
